import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaPlus, FaTrash, FaSearch, FaTh, FaList, FaEdit, FaRegClock, FaSortAmountDown, FaSortAlphaDown } from 'react-icons/fa';
import Footer from '../components/Footer';
import ConfirmationModal from '../components/ConfirmationModal';

interface Project {
  _id: string;
  name: string;
  preview: string;
  updatedAt: string;
}

const PROJECTS_PER_PAGE = 12;

const formatTimeAgo = (dateStr: string): string => {
  const now = Date.now();
  const date = new Date(dateStr).getTime();
  const diff = now - date;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: days > 365 ? 'numeric' : undefined,
  });
};

const Projects: React.FC = () => {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [loadingPreviews, setLoadingPreviews] = useState<{ [key: string]: boolean }>({});
  const [visibleCount, setVisibleCount] = useState(PROJECTS_PER_PAGE);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortMode, setSortMode] = useState<'recent' | 'name'>('recent');
  const [mounted, setMounted] = useState(false);
  const [deleteModal, setDeleteModal] = useState<{ isOpen: boolean; projectId: string | null; projectName: string }>({
    isOpen: false,
    projectId: null,
    projectName: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    requestAnimationFrame(() => setMounted(true));
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          setError('No authentication token found');
          setLoading(false);
          return;
        }

        const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';

        const response = await fetch(`${API_URL}/api/artboards`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }

        const data = await response.json();
        setProjects(data);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setError('Error fetching projects');
        setLoading(false);
      }
    };

    fetchProjects();
  }, []);

  const filteredProjects = projects
    .filter(project =>
      project.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => {
      if (sortMode === 'name') {
        return a.name.localeCompare(b.name);
      }
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });

  const handleDeleteClick = (projectId: string, projectName: string) => {
    setDeleteModal({
      isOpen: true,
      projectId,
      projectName
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModal.projectId) return;

    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5001';
      const response = await fetch(`${API_URL}/api/artboards/${deleteModal.projectId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to delete project');
      }

      setProjects(projects.filter(project => project._id !== deleteModal.projectId));
      setDeleteModal({ isOpen: false, projectId: null, projectName: '' });
    } catch (error) {
      console.error('Error deleting project:', error);
      setDeleteModal({ isOpen: false, projectId: null, projectName: '' });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModal({ isOpen: false, projectId: null, projectName: '' });
  };

  const handlePreviewLoad = (projectId: string) => {
    setLoadingPreviews(prev => ({
      ...prev,
      [projectId]: false
    }));
  };

  const handlePreviewError = (projectId: string, e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    const imgElement = e.currentTarget;
    const project = projects.find(p => p._id === projectId);

    if (project?.preview && project.preview.startsWith('data:image/png;base64,')) {
      imgElement.src = project.preview;
      return;
    }

    imgElement.style.display = 'none';

    setLoadingPreviews(prev => ({
      ...prev,
      [projectId]: false
    }));
  };

  // --- Loading state with skeleton cards ---
  if (loading) {
    return (
      <div className="relative min-h-[calc(100vh-60px)] overflow-hidden bg-[#fafafa] dark:bg-[#0a0a0a]" style={{ fontFamily: "'Geist', sans-serif" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-[30%] right-[10%] h-[60%] w-[50%] rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.06),transparent_70%)] blur-[80px] dark:bg-[radial-gradient(circle,rgba(99,102,241,0.1),transparent_70%)]" />
          <div className="absolute -bottom-[20%] left-[5%] h-[50%] w-[40%] rounded-full bg-[radial-gradient(circle,rgba(168,85,247,0.05),transparent_70%)] blur-[80px] dark:bg-[radial-gradient(circle,rgba(168,85,247,0.08),transparent_70%)]" />
        </div>
        <div className="relative mx-auto w-full max-w-[1400px] px-6 py-10 sm:px-8 lg:px-12">
          {/* Skeleton header */}
          <div className="mb-10">
            <div className="h-9 w-48 animate-pulse rounded-xl bg-gray-200/80 dark:bg-gray-800" />
            <div className="mt-3 h-5 w-64 animate-pulse rounded-lg bg-gray-100/80 dark:bg-gray-800/60" />
            <div className="mt-8 flex gap-3">
              <div className="h-12 flex-1 animate-pulse rounded-xl bg-gray-100/80 dark:bg-gray-800/60" />
              <div className="h-12 w-24 animate-pulse rounded-xl bg-gray-100/80 dark:bg-gray-800/60" />
            </div>
          </div>
          {/* Skeleton grid */}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {[...Array(8)].map((_, i) => (
              <div key={i} className="overflow-hidden rounded-2xl border border-gray-200/40 bg-white dark:border-gray-800/40 dark:bg-gray-900/50" style={{ animationDelay: `${i * 80}ms` }}>
                <div className="aspect-[16/10] animate-pulse bg-gray-100 dark:bg-gray-800" />
                <div className="p-4">
                  <div className="h-4 w-3/4 animate-pulse rounded-lg bg-gray-100 dark:bg-gray-800" />
                  <div className="mt-2.5 h-3 w-1/3 animate-pulse rounded-lg bg-gray-50 dark:bg-gray-800/60" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (error) {
    return (
      <div className="relative min-h-[calc(100vh-60px)] overflow-hidden bg-[#fafafa] dark:bg-[#0a0a0a]" style={{ fontFamily: "'Geist', sans-serif" }}>
        <div className="pointer-events-none absolute inset-0">
          <div className="absolute -top-[30%] right-[10%] h-[60%] w-[50%] rounded-full bg-[radial-gradient(circle,rgba(99,102,241,0.06),transparent_70%)] blur-[80px]" />
        </div>
        <div className="relative flex min-h-[60vh] flex-col items-center justify-center px-6">
          <div className="mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-500/10 dark:to-orange-500/10">
            <svg className="h-9 w-9 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
            </svg>
          </div>
          <h2 className="mb-2 text-xl font-bold text-gray-900 dark:text-white">Something went wrong</h2>
          <p className="mb-8 max-w-sm text-center text-sm leading-relaxed text-gray-500 dark:text-gray-400">{error}</p>
          <Link
            to="/editor"
            className="group relative inline-flex items-center gap-2.5 overflow-hidden rounded-xl px-6 py-3 text-sm font-bold uppercase tracking-wider text-white transition-all hover:-translate-y-0.5 hover:shadow-[0_12px_35px_rgba(99,102,241,0.35)] active:translate-y-0"
            style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)' }}
          >
            <FaPlus size={12} />
            New Project
          </Link>
        </div>
      </div>
    );
  }

  // --- Main content ---
  return (
    <div className="relative flex min-h-[calc(100vh-60px)] flex-col overflow-hidden bg-[#fafafa] dark:bg-[#0a0a0a]" style={{ fontFamily: "'Geist', sans-serif" }}>
      {/* Ambient background */}
      <div className="pointer-events-none absolute inset-0">
        <div
          className="absolute -top-[30%] right-[10%] h-[60%] w-[50%] rounded-full opacity-100 blur-[80px]"
          style={{
            background: 'radial-gradient(circle, rgba(99,102,241,0.06), transparent 70%)',
            animation: 'projectsPulse 12s ease-in-out infinite alternate',
          }}
        />
        <div
          className="absolute -bottom-[20%] left-[5%] h-[50%] w-[40%] rounded-full opacity-100 blur-[80px]"
          style={{
            background: 'radial-gradient(circle, rgba(168,85,247,0.05), transparent 70%)',
            animation: 'projectsPulse 15s ease-in-out 3s infinite alternate',
          }}
        />
      </div>

      <div className="relative mx-auto w-full max-w-[1400px] flex-1 px-6 py-10 sm:px-8 lg:px-12">

        {/* Header */}
        <div
          className="mb-10"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(20px)',
            transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
          }}
        >
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-[-0.5px] text-gray-900 dark:text-white sm:text-4xl">
                My Projects
              </h1>
              <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
                {projects.length > 0
                  ? <>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-md bg-indigo-100 px-1.5 text-[11px] font-bold text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-400">
                          {filteredProjects.length}
                        </span>
                        {filteredProjects.length === 1 ? 'artboard' : 'artboards'}{searchTerm ? ' found' : ' in your workspace'}
                      </span>
                    </>
                  : 'Your creative workspace'
                }
              </p>
            </div>
            <Link
              to="/editor"
              className="group relative inline-flex items-center gap-2.5 self-start overflow-hidden rounded-xl px-5 py-3 text-sm font-bold text-white shadow-[0_4px_20px_rgba(99,102,241,0.3)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(99,102,241,0.4)] active:translate-y-0 sm:px-6"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)' }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 opacity-0 transition-opacity group-hover:opacity-100" style={{ transform: 'skewX(-20deg)' }} />
              <FaPlus size={11} />
              <span className="hidden sm:inline">New Project</span>
              <span className="sm:hidden">New</span>
            </Link>
          </div>

          {/* Search, Sort & View Toggle */}
          {projects.length > 0 && (
            <div
              className="mt-8 flex items-center gap-2 sm:gap-3"
              style={{
                opacity: mounted ? 1 : 0,
                transform: mounted ? 'translateY(0)' : 'translateY(12px)',
                transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.15s',
              }}
            >
              {/* Search */}
              <div className="relative flex-1">
                <FaSearch className="pointer-events-none absolute left-4 top-1/2 z-10 -translate-y-1/2 text-gray-400 dark:text-gray-500" size={13} />
                <input
                  type="text"
                  placeholder="Search projects..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-xl border-2 border-gray-200/80 bg-white/80 py-3 pl-11 pr-4 text-sm text-gray-900 outline-none backdrop-blur-sm transition-all placeholder:text-gray-400 focus:border-indigo-500/50 focus:bg-white focus:shadow-[0_0_0_3px_rgba(99,102,241,0.08)] dark:border-gray-700/60 dark:bg-gray-800/60 dark:text-white dark:placeholder:text-gray-500 dark:focus:border-indigo-500/50 dark:focus:bg-gray-800"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-gray-400 transition-colors hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <svg className="h-3.5 w-3.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                )}
              </div>

              {/* Sort Toggle */}
              <button
                onClick={() => setSortMode(sortMode === 'recent' ? 'name' : 'recent')}
                title={sortMode === 'recent' ? 'Sorted by recent' : 'Sorted by name'}
                className="flex h-[46px] items-center gap-1.5 rounded-xl border-2 border-gray-200/80 bg-white/80 px-3.5 text-xs font-medium text-gray-500 backdrop-blur-sm transition-all hover:border-gray-300 hover:text-gray-700 dark:border-gray-700/60 dark:bg-gray-800/60 dark:text-gray-400 dark:hover:border-gray-600 dark:hover:text-gray-200 sm:px-4"
              >
                {sortMode === 'recent' ? <FaSortAmountDown size={13} /> : <FaSortAlphaDown size={13} />}
                <span className="hidden sm:inline">{sortMode === 'recent' ? 'Recent' : 'A-Z'}</span>
              </button>

              {/* View Toggle */}
              <div className="flex overflow-hidden rounded-xl border-2 border-gray-200/80 bg-white/80 backdrop-blur-sm dark:border-gray-700/60 dark:bg-gray-800/60">
                <button
                  onClick={() => setViewMode('grid')}
                  title="Grid view"
                  className={`flex h-[42px] items-center gap-1.5 px-3.5 text-xs font-medium transition-all ${
                    viewMode === 'grid'
                      ? 'bg-indigo-500 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-200'
                  }`}
                >
                  <FaTh size={12} />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  title="List view"
                  className={`flex h-[42px] items-center gap-1.5 px-3.5 text-xs font-medium transition-all ${
                    viewMode === 'list'
                      ? 'bg-indigo-500 text-white shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]'
                      : 'text-gray-500 hover:bg-gray-50 hover:text-gray-700 dark:text-gray-400 dark:hover:bg-gray-700/50 dark:hover:text-gray-200'
                  }`}
                >
                  <FaList size={12} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Empty search results */}
        {filteredProjects.length === 0 && projects.length > 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-center">
            <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-gray-100 dark:bg-gray-800">
              <FaSearch className="text-gray-400" size={20} />
            </div>
            <h2 className="mb-2 text-lg font-bold text-gray-900 dark:text-white">No results found</h2>
            <p className="max-w-xs text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              No projects match "<span className="font-semibold text-gray-700 dark:text-gray-300">{searchTerm}</span>"
            </p>
            <button
              onClick={() => setSearchTerm('')}
              className="mt-4 text-sm font-medium text-indigo-500 transition-colors hover:text-indigo-600"
            >
              Clear search
            </button>
          </div>

        /* Empty state - no projects */
        ) : projects.length === 0 ? (
          <div
            className="flex flex-col items-center justify-center py-24 text-center"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateY(0)' : 'translateY(20px)',
              transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1) 0.2s',
            }}
          >
            <div className="relative mb-8">
              <div className="flex h-28 w-28 items-center justify-center rounded-3xl border-2 border-dashed border-gray-200 bg-gradient-to-br from-indigo-50 to-purple-50 dark:border-gray-700 dark:from-indigo-500/10 dark:to-purple-500/10">
                <svg className="h-12 w-12 text-indigo-400/60" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 15.75l5.159-5.159a2.25 2.25 0 013.182 0l5.159 5.159m-1.5-1.5l1.409-1.409a2.25 2.25 0 013.182 0l2.909 2.909M3.75 21h16.5A2.25 2.25 0 0022.5 18.75V5.25A2.25 2.25 0 0020.25 3H3.75A2.25 2.25 0 001.5 5.25v13.5A2.25 2.25 0 003.75 21z" />
                </svg>
              </div>
              <div className="absolute -bottom-1.5 -right-1.5 flex h-9 w-9 items-center justify-center rounded-full text-white shadow-lg shadow-indigo-500/30" style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6)' }}>
                <FaPlus size={11} />
              </div>
            </div>
            <h2 className="mb-2 text-2xl font-bold tracking-[-0.5px] text-gray-900 dark:text-white">Create your first project</h2>
            <p className="mb-8 max-w-md text-sm leading-relaxed text-gray-500 dark:text-gray-400">
              Start designing by creating a new artboard. Your projects will appear here, ready for you to edit anytime.
            </p>
            <Link
              to="/editor"
              className="group relative inline-flex items-center gap-2.5 overflow-hidden rounded-xl px-7 py-3.5 text-sm font-bold text-white shadow-[0_4px_20px_rgba(99,102,241,0.35)] transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(99,102,241,0.45)] active:translate-y-0"
              style={{ background: 'linear-gradient(135deg, #6366f1, #8b5cf6, #a855f7)' }}
            >
              <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/15 to-white/0 opacity-0 transition-opacity group-hover:opacity-100" style={{ transform: 'skewX(-20deg)' }} />
              <FaPlus size={12} />
              Create Project
            </Link>
          </div>

        /* Project grid / list */
        ) : (
          <>
            {viewMode === 'grid' ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {filteredProjects.slice(0, visibleCount).map((project, index) => (
                  <div
                    key={project._id}
                    onClick={() => navigate(`/editor/${project._id}`)}
                    className="group relative cursor-pointer overflow-hidden rounded-2xl border border-gray-200/60 bg-white transition-all duration-300 hover:-translate-y-1 hover:border-indigo-200/60 hover:shadow-[0_20px_50px_rgba(99,102,241,0.1)] dark:border-gray-800/60 dark:bg-gray-900/80 dark:hover:border-indigo-500/20 dark:hover:shadow-[0_20px_50px_rgba(99,102,241,0.08)]"
                    style={{
                      opacity: mounted ? 1 : 0,
                      transform: mounted ? 'translateY(0)' : 'translateY(16px)',
                      transition: `all 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.05 * Math.min(index, 11)}s`,
                    }}
                  >
                    {/* Thumbnail */}
                    <div className="relative aspect-[16/10] overflow-hidden bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-850">
                      {loadingPreviews[project._id] && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-200 border-t-indigo-500" />
                        </div>
                      )}
                      <img
                        key={project._id + project.updatedAt}
                        src={`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/public/artboards/${project._id}/thumbnail?t=${project.updatedAt}`}
                        alt={project.name}
                        loading="lazy"
                        onLoad={() => handlePreviewLoad(project._id)}
                        onError={(e) => handlePreviewError(project._id, e)}
                        className="h-full w-full select-none object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                      {/* Hover overlay */}
                      <div className="absolute inset-0 flex items-end bg-gradient-to-t from-black/60 via-black/10 to-transparent opacity-0 transition-all duration-300 group-hover:opacity-100">
                        <div className="flex w-full items-center justify-between p-3.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/editor/${project._id}`);
                            }}
                            className="flex items-center gap-1.5 rounded-lg bg-white/20 px-3 py-1.5 text-xs font-medium text-white backdrop-blur-md transition-all hover:bg-white/30"
                          >
                            <FaEdit size={10} />
                            Edit
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(project._id, project.name);
                            }}
                            className="flex h-7 w-7 items-center justify-center rounded-lg bg-white/15 text-white/80 backdrop-blur-md transition-all hover:bg-red-500/80 hover:text-white"
                          >
                            <FaTrash size={10} />
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Info */}
                    <div className="px-4 py-3.5">
                      <h3 className="truncate text-[13px] font-semibold text-gray-900 dark:text-white">
                        {project.name}
                      </h3>
                      <p className="mt-1 flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                        <FaRegClock size={9} />
                        {formatTimeAgo(project.updatedAt)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              /* List view */
              <div
                className="overflow-hidden rounded-2xl border border-gray-200/60 bg-white dark:border-gray-800/60 dark:bg-gray-900/80"
                style={{
                  opacity: mounted ? 1 : 0,
                  transform: mounted ? 'translateY(0)' : 'translateY(16px)',
                  transition: 'all 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.1s',
                }}
              >
                {filteredProjects.slice(0, visibleCount).map((project, index) => (
                  <div
                    key={project._id}
                    onClick={() => navigate(`/editor/${project._id}`)}
                    className={`group flex cursor-pointer items-center gap-4 px-5 py-3.5 transition-all hover:bg-indigo-50/40 dark:hover:bg-indigo-500/5 ${
                      index > 0 ? 'border-t border-gray-100 dark:border-gray-800/50' : ''
                    }`}
                  >
                    {/* Thumbnail */}
                    <div className="h-12 w-[76px] flex-shrink-0 overflow-hidden rounded-lg bg-gradient-to-br from-gray-50 to-gray-100 ring-1 ring-gray-200/50 dark:from-gray-800 dark:to-gray-850 dark:ring-gray-700/30">
                      <img
                        key={project._id + project.updatedAt}
                        src={`${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/public/artboards/${project._id}/thumbnail?t=${project.updatedAt}`}
                        alt={project.name}
                        loading="lazy"
                        onLoad={() => handlePreviewLoad(project._id)}
                        onError={(e) => handlePreviewError(project._id, e)}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                    </div>
                    {/* Info */}
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-sm font-semibold text-gray-900 transition-colors group-hover:text-indigo-600 dark:text-white dark:group-hover:text-indigo-400">
                        {project.name}
                      </h3>
                      <p className="mt-0.5 flex items-center gap-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                        <FaRegClock size={9} />
                        Edited {formatTimeAgo(project.updatedAt)}
                      </p>
                    </div>
                    {/* Actions */}
                    <div className="flex items-center gap-1.5 opacity-0 transition-all group-hover:opacity-100">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/editor/${project._id}`);
                        }}
                        className="flex items-center gap-1.5 rounded-lg bg-indigo-50 px-3 py-1.5 text-xs font-medium text-indigo-600 transition-all hover:bg-indigo-100 dark:bg-indigo-500/10 dark:text-indigo-400 dark:hover:bg-indigo-500/20"
                      >
                        <FaEdit size={10} />
                        Edit
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteClick(project._id, project.name);
                        }}
                        className="flex h-7 w-7 items-center justify-center rounded-lg text-gray-400 transition-all hover:bg-red-50 hover:text-red-500 dark:hover:bg-red-500/10 dark:hover:text-red-400"
                      >
                        <FaTrash size={10} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Load more */}
            {visibleCount < filteredProjects.length && (
              <div className="mt-10 text-center">
                <button
                  onClick={() => setVisibleCount((prev) => prev + PROJECTS_PER_PAGE)}
                  className="inline-flex items-center gap-2 rounded-xl border-2 border-gray-200/80 bg-white/80 px-6 py-3 text-sm font-semibold text-gray-700 backdrop-blur-sm transition-all hover:-translate-y-0.5 hover:border-indigo-200 hover:shadow-md dark:border-gray-700/80 dark:bg-gray-800/80 dark:text-gray-300 dark:hover:border-indigo-500/30"
                >
                  Load More
                  <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-md bg-gray-100 px-1.5 text-[11px] font-bold text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                    {filteredProjects.length - visibleCount}
                  </span>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />

      <ConfirmationModal
        isOpen={deleteModal.isOpen}
        onConfirm={handleDeleteConfirm}
        onClose={handleDeleteCancel}
        title={`Delete "${deleteModal.projectName}"`}
        message="This artboard will be permanently deleted. This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        type="danger"
      />

      <style>{`
        @keyframes projectsPulse {
          0% { transform: translate(0, 0) scale(1); opacity: 0.6; }
          100% { transform: translate(20px, -15px) scale(1.05); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Projects;
